import type {
  LabelElement,
  LabelLayoutConfig,
  PrintData,
} from 'src/app/services/niimbot-printer/niimbot-printer.models';

/**
 * Ações da impressão de etiqueta — sem Angular, sem template.
 *
 * O componente (`checkin-etiqueta.component.ts`) é só a casca visual: escolhe
 * diálogo ou bottom sheet, desenha cabeçalho/rodapé e chama daqui. Quem
 * decidir mudar o visual não precisa entender a montagem do layout, e quem
 * mexer na montagem não precisa abrir o template — foi por isso que a divisão
 * existe.
 *
 * `layoutSalvo` é a única função que toca o navegador (localStorage). O resto
 * é puro, e é nele que mora o teste.
 */

/**
 * Onde o `app-label-editor` grava o layout desenhado. O nome é dele; está
 * repetido aqui porque o editor não expõe a chave, e um layout salvo por uma
 * tela tem que ser lido pela outra.
 */
export const CHAVE_LAYOUT = 'niimbot-label-layout';

/** O que a etiqueta precisa saber do ingresso. Recorte do objeto cru da API. */
export interface IngressoEtiqueta {
  codigoIngresso?: string;
  nomeParticipante?: string;
  nomeLote?: string;
}

/**
 * Por que a impressão não pode acontecer agora.
 *
 * Isto existe porque o bug era exatamente a AUSÊNCIA disto: o
 * `imprimirDireto()` antigo abria com
 * `if (!ingresso || !conectada) return` — sem impressora, o clique não fazia
 * nada e não dizia nada. Nomear o impedimento é o que deixa a tela desabilitar
 * o botão e explicar o motivo em vez de engolir o clique.
 */
export interface Impedimento {
  motivo: 'sem-impressora' | 'sem-layout' | 'sem-selecao';
  titulo: string;
  detalhe: string;
  /** Se existe uma saída de um clique só, qual é. */
  acao: 'conectar' | null;
}

const IMPEDIMENTOS: Record<Impedimento['motivo'], Omit<Impedimento, 'motivo'>> = {
  'sem-impressora': {
    titulo: 'Nenhuma impressora conectada',
    detalhe: 'Conecte a impressora térmica para imprimir esta etiqueta.',
    acao: 'conectar',
  },
  'sem-layout': {
    titulo: 'Layout da etiqueta não configurado',
    detalhe: 'Monte a etiqueta no editor abaixo antes de imprimir.',
    acao: null,
  },
  // Serve ao lote e ao individual: a tela guarda uma LISTA nos dois casos, e
  // lista vazia é a mesma pergunta sem resposta ("imprimir o quê?"). O caminho
  // individual nunca abre vazio, então a redação fala do que de fato acontece —
  // a seleção some quando se pagina ou filtra depois de marcar.
  'sem-selecao': {
    titulo: 'Nenhum ingresso para imprimir',
    detalhe: 'Selecione ao menos um ingresso na lista e abra a etiqueta de novo.',
    acao: null,
  },
};

/**
 * O primeiro impedimento, ou `null` quando dá para imprimir.
 *
 * A ordem importa: sem impressora é o caso comum e o que tem saída de um
 * clique, então vem antes do layout.
 */
export function impedimento(estado: {
  conectada: boolean;
  ingressos: readonly IngressoEtiqueta[];
  layout: LabelLayoutConfig | null;
}): Impedimento | null {
  const motivo: Impedimento['motivo'] | null = estado.ingressos.length === 0
    ? 'sem-selecao'
    : !estado.conectada
      ? 'sem-impressora'
      : !estado.layout
        ? 'sem-layout'
        : null;
  return motivo ? { motivo, ...IMPEDIMENTOS[motivo] } : null;
}

/** Layout desenhado no editor e guardado pelo navegador. */
export function layoutSalvo(): LabelLayoutConfig | null {
  try {
    return JSON.parse(localStorage.getItem(CHAVE_LAYOUT) ?? 'null');
  } catch {
    // localStorage bloqueado ou JSON corrompido: para a tela é o mesmo que não
    // ter layout, e o impedimento já cobre isso.
    return null;
  }
}

/**
 * Campos que o produtor preenche na hora da impressão (`dataSource: 'extra'`)
 * — os outros vêm do ingresso e não são digitáveis.
 */
export function camposExtras(layout: LabelLayoutConfig | null): LabelElement[] {
  return (layout?.elements ?? []).filter((e) => e.dataSource === 'extra');
}

/**
 * Cópia do layout com os valores extras aplicados.
 *
 * Cópia, e não mutação: o `layout` vem do editor (ou do localStorage) e segue
 * sendo usado depois da impressão — escrever nele faria o valor digitado de um
 * ingresso vazar para o próximo.
 */
export function montarLayout(
  base: LabelLayoutConfig,
  valoresExtras: Readonly<Record<string, string>>,
): LabelLayoutConfig {
  const copia: LabelLayoutConfig = structuredClone(base);
  for (const el of copia.elements) {
    if (el.dataSource === 'extra' && valoresExtras[el.id] !== undefined) {
      el.value = valoresExtras[el.id];
    }
  }
  return copia;
}

/** Valores iniciais dos campos extras, preservando o que já foi digitado. */
export function valoresIniciaisExtras(
  layout: LabelLayoutConfig | null,
  atuais: Readonly<Record<string, string>>,
): Record<string, string> {
  const proximos: Record<string, string> = { ...atuais };
  for (const el of camposExtras(layout)) {
    if (!(el.id in proximos)) proximos[el.id] = el.value ?? '';
  }
  return proximos;
}

/**
 * O que a impressora recebe do ingresso.
 *
 * `sobrescritos` vem do duplo clique no canvas: o editor guarda a edição só
 * localmente e AVISA por output. Sem passar por aqui, o operador corrigia
 * "Padaria Teste Claude" para algo curto, via a correção no preview e recebia
 * o nome completo no papel — o valor exibido e o valor impresso eram duas
 * fontes diferentes.
 */
export function dadosImpressao(
  ingresso: IngressoEtiqueta,
  sobrescritos: { nomeParticipante?: string | null; nomeLote?: string | null } = {},
): PrintData {
  return {
    qrText: ingresso.codigoIngresso ?? '',
    participantName: sobrescritos.nomeParticipante ?? ingresso.nomeParticipante ?? '',
    loteName: sobrescritos.nomeLote ?? ingresso.nomeLote ?? '',
  };
}

/** Subtítulo do cabeçalho: quem, qual código, qual lote. */
export function resumoIngresso(ingresso: IngressoEtiqueta | null): string {
  if (!ingresso) return '';
  return [ingresso.nomeParticipante, ingresso.codigoIngresso, ingresso.nomeLote]
    .filter((p): p is string => !!p)
    .join(' · ');
}

/**
 * Subtítulo do cabeçalho quando a seleção é o assunto.
 *
 * Um por um continua mostrando quem é (`resumoIngresso`); num lote os nomes
 * não cabem e o que o operador precisa saber é QUANTOS vão sair.
 */
export function resumoSelecao(ingressos: readonly IngressoEtiqueta[]): string {
  if (ingressos.length === 1) return resumoIngresso(ingressos[0]);
  return `${ingressos.length} ingressos selecionados`;
}

/** Onde a impressão do lote parou e por quê. */
export interface ResultadoLote {
  impressas: number;
  /** Ingresso que falhou e a mensagem. `null` quando não houve falha. */
  falha: { ingresso: IngressoEtiqueta; erro: string } | null;
  /** O operador clicou em Parar. */
  interrompido: boolean;
}

function mensagemErro(erro: unknown): string {
  const msg = (erro as { message?: string })?.message;
  return msg || 'Não foi possível imprimir a etiqueta.';
}

/**
 * Imprime a seleção, UMA etiqueta por vez.
 *
 * O `await` dentro do `for` é o ponto da função, não um detalhe de estilo: a
 * impressora fala por GATT e aceita uma operação por vez — um `Promise.all` (ou
 * um `forEach` com async dentro) dispara tudo junto e a segunda etiqueta morre
 * com "GATT operation already in progress".
 *
 * Para na PRIMEIRA falha em vez de seguir tentando: quando a conexão cai ou o
 * papel acaba, as etiquetas seguintes falhariam igual e o operador levaria N
 * erros para descobrir um problema só. O `ResultadoLote` diz quantas saíram, e
 * é com esse número que ele reduz a seleção e retoma.
 *
 * `parar()` é consultado ANTES de cada etiqueta, nunca no meio: cortar uma
 * impressão em curso deixa a impressora num estado que a próxima conexão
 * precisa limpar.
 */
export async function imprimirEmLote(
  ingressos: readonly IngressoEtiqueta[],
  deps: {
    imprimir: (ingresso: IngressoEtiqueta) => Promise<void>;
    /** Antes de mandar cada etiqueta — é daqui que sai o "etiqueta 3 de 12". */
    aoIniciar?: (indice: number, ingresso: IngressoEtiqueta) => void;
    parar?: () => boolean;
  },
): Promise<ResultadoLote> {
  let impressas = 0;

  for (const [indice, ingresso] of ingressos.entries()) {
    if (deps.parar?.()) return { impressas, falha: null, interrompido: true };
    deps.aoIniciar?.(indice, ingresso);
    try {
      await deps.imprimir(ingresso);
    } catch (erro) {
      return { impressas, falha: { ingresso, erro: mensagemErro(erro) }, interrompido: false };
    }
    impressas++;
  }

  return { impressas, falha: null, interrompido: false };
}

/**
 * Percentual do lote inteiro, contando a etiqueta em curso.
 *
 * `atualPct` vem do `printProgress$` da impressora (0–100 da etiqueta que está
 * saindo agora). Somar a fração parcial é o que faz a barra andar durante uma
 * etiqueta em vez de pular de degrau em degrau — com poucos ingressos, uma
 * barra que só se move a cada conclusão parece travada.
 */
export function percentualLote(concluidas: number, total: number, atualPct: number): number {
  if (total <= 0) return 0;
  const parcial = concluidas < total ? Math.min(Math.max(atualPct, 0), 100) / 100 : 0;
  return Math.min(Math.round(((concluidas + parcial) / total) * 100), 100);
}

/** O que dizer ao operador quando o lote termina (ou para). */
export function avisoResultadoLote(
  resultado: ResultadoLote,
  total: number,
): { icon: 'success' | 'warning' | 'error'; title: string; text?: string } {
  const restantes = total - resultado.impressas;

  if (resultado.falha && total === 1) {
    // Uma etiqueta só: "parou na etiqueta 1 de 1" é uma contagem sem sentido —
    // o operador quer o erro, não a estatística.
    return { icon: 'error', title: 'Erro ao imprimir', text: resultado.falha.erro };
  }

  if (resultado.falha) {
    return {
      icon: 'error',
      title: `Parou na etiqueta ${resultado.impressas + 1} de ${total}`,
      // O nome de quem falhou importa: é por ele que o operador reencontra a
      // linha na tabela para recomeçar dali.
      text: `${resultado.falha.erro} ${resultado.impressas} etiqueta(s) saíram; ${restantes} ficaram, a partir de ${resultado.falha.ingresso.nomeParticipante || resultado.falha.ingresso.codigoIngresso || 'este ingresso'}.`,
    };
  }

  if (resultado.interrompido) {
    return {
      icon: 'warning',
      title: 'Impressão interrompida',
      text: `${resultado.impressas} de ${total} etiqueta(s) impressa(s); ${restantes} não saíram.`,
    };
  }

  return { icon: 'success', title: `${resultado.impressas} etiqueta(s) impressa(s)` };
}
